import Vue from 'vue';
import { mapGetters } from 'vuex';
import Project from 'src/api/project';
import DataUtils from 'src/utils/data';
import DownloadExportButton from './widgets/DownloadExportButton.vue';
import HelpIcon from './widgets/HelpIcon.vue';
import Storyboard from './widgets/Storyboard.vue';
import TopicInfoList from './widgets/TopicInfoList.vue';
import { BfSlider } from 'components/Butterfly';
import ToggleSlider from "components/widgets/ToggleSlider.vue";
import QueryUtils from 'src/utils/query';
import ResizeListener from "components/widgets/ResizeListener/ResizeListener.vue";
import { isEqual } from 'lodash';
const StoryboardPage = Vue.extend({
    components: { DownloadExportButton, HelpIcon, Storyboard, TopicInfoList, BfSlider, ToggleSlider, ResizeListener },
    props: {
        showConceptList: { type: Boolean, required: false, default: true },
        onConceptClicked: { type: Function, required: false, default: null },
        defaultDimAddedConcepts: { type: Boolean, required: false, default: false },
        // Optional replacement for savedQueries
        queryList: { type: Array, required: false, default: () => [] },
        // Additional concepts to dim
        dimmedConcepts: { type: Array, required: false, default: () => [] },
    },
    data() {
        return {
            activeConcept: null,
            layoutComplete: false,
            numConcepts: 0,
            numClusters: 0,
            storyboardHelp: '<p>The storyboard is designed to help you understand the language model that has been built from your text data.</p>' +
                '<p>Concepts are represented by colored circles with a label. The size of the circle and label indicate how frequently it is mentioned.</p>' +
                '<p>Clicking a concept will navigate to the search page, which provides access to more information about that concept.</p>' +
                '<p>The colored hulls represent soft groupings of related concepts. The groupings can be made more or less specific using the "# of Colors" slider.</p>' +
                '<p>The "# of Concepts" slider allows concepts to be hidden based on their frequency. Starting with just a subset of the most frequent concepts can help in building an understanding of larger, more complex storyboards.</p>' +
                '<p>The lines between concepts represent the strongest relationships. The smallest possible number of links are drawn such that every concept is connected. Follow the links to help understand key narratives within the data.</p>',
            conceptlistHelp: '<p>This list shows all concepts present in the storyboard to the left. The number of verbatims each concept is mentioned in is represented via width of the colored bars and indicated as a percentage adjacent to each bar.</p>' +
                '<p>This list can be sorted by the colored groupings shown in the storyboard or by the frequency of the concepts themselves.</p>' +
                '<p>Hovering a concept or its frequency bar will highlight that concept on the storyboard. You can also click through to the search page for that concept.</p>',
            MIN_CLUSTERS: DataUtils.MIN_CLUSTERS,
            MAX_CLUSTERS: DataUtils.MAX_CLUSTERS,
            dimAddedConcepts: this.defaultDimAddedConcepts,
        };
    },
    computed: {
        ...mapGetters(['currentAnalysis', 'currentModel', 'savedQueries']),
        maxConcepts() {
            return !this.currentModel?.concept_layout ? 0 : Object.keys(this.currentModel.concept_layout).length;
        },
        conceptsToDim() {
            let concepts = [];
            const queries = this.queryList.length
                ? this.queryList
                : this.savedQueries;
            queries.forEach(sq => {
                const nodes = QueryUtils.getQueryLeafNodesFromBotanicQuery(sq.query_value);
                concepts = concepts.concat(nodes?.filter(n => n.type === 'text' && n.value).map(n => n.value) ?? []);
            });
            return concepts.concat(this.dimmedConcepts);
        }
    },
    watch: {
        conceptsToDim(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.$nextTick(() => {
                    this.$refs.storyboard.draw();
                });
            }
        },
        numClusters(value) {
            this.$refs.storyboard.updateClusters(value);
            if (this.saveClustersTimer) {
                clearTimeout(this.saveClustersTimer);
            }
            this.saveClustersTimer = setTimeout(() => {
                Project.updateAnalysisDisplayAttributes(this.$store, { numClusters: value });
            }, 700);
        },
        numConcepts(value) {
            this.$refs.storyboard.updateNumConcepts(value);
            if (this.saveConceptsTimer) {
                clearTimeout(this.saveConceptsTimer);
            }
            this.saveConceptsTimer = setTimeout(() => {
                Project.updateAnalysisDisplayAttributes(this.$store, { numConcepts: value });
            }, 700);
        }
    },
    beforeDestroy() {
        clearTimeout(this.saveConceptsTimer);
        clearTimeout(this.saveClustersTimer);
    },
    mounted() {
        this.numConcepts = this.currentModel?.numConceptsDisplayed;
        this.numClusters = this.currentModel?.clusters?.length;
        this.setSizes();
        this.$nextTick(() => {
            this.$refs.storyboard.draw();
        });
    },
    metaInfo() {
        return {
            title: this.currentAnalysis ? `${this.currentAnalysis.name} Storyboard - Kapiche` : null
        };
    },
    methods: {
        emitConceptClick(concept, el) {
            if (this.onConceptClicked) {
                const dimmed = this.conceptsToDim.includes(concept);
                this.onConceptClicked(concept, el, dimmed);
            }
        },
        setSizes() {
            this.$refs.storyboard.draw();
        },
        activateConcept(concept) {
            this.activeConcept = concept;
        },
        getEl() {
            return this.$refs.storyboard.$el.querySelector('svg');
        },
        getExportConfig(viewableOnly = false) {
            return this.$refs.storyboard.getExportConfig(viewableOnly);
        },
        getConceptListCsvData() {
            return this.$refs.conceptInfoList.getCsvData();
        },
        onLayoutComplete() {
            document.getElementById('storyboard-dimmer').classList.remove('active');
            this.layoutComplete = true;
        },
        conceptClicked(concept) {
            this.$analytics.track.analysis.conceptClickedOnStoryboard(concept);
        }
    }
});
export default StoryboardPage;
